/**
 * Canonical public restaurant URLs for printable / shareable QR codes.
 * Always appends ?source=… for attribution (Insights qr_scan + analytics).
 */

export const RESTAURANT_PUBLIC_BASE_URL = "https://menuhunter.app";

export type QrAttributionKind = "qr" | "sticker";

function normalizeRestaurantId(restaurantId: string): string {
  return typeof restaurantId === "string" ? restaurantId.trim() : "";
}

function normalizeBase(origin?: string): string {
  return (origin ?? RESTAURANT_PUBLIC_BASE_URL).replace(/\/$/, "");
}

function firstSourceValue(
  source: string | string[] | null | undefined
): string | null {
  if (source == null) return null;
  const raw = Array.isArray(source) ? source[0] : source;
  if (typeof raw !== "string") return null;
  const normalized = raw.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

/** Share / generic QR: `?source=qr` */
export function buildRestaurantPublicQrUrl(
  restaurantId: string,
  origin?: string
): string {
  const id = normalizeRestaurantId(restaurantId);
  if (!id) return "";
  const base = normalizeBase(origin);
  return `${base}/restaurant/${encodeURIComponent(id)}?source=qr`;
}

/** Print sticker QR: `?source=qr_sticker` (analytics: qr_source=sticker) */
export function buildRestaurantStickerQrUrl(
  restaurantId: string,
  origin?: string
): string {
  const id = normalizeRestaurantId(restaurantId);
  if (!id) return "";
  const base = normalizeBase(origin);
  return `${base}/restaurant/${encodeURIComponent(id)}?source=qr_sticker`;
}

/**
 * True for any QR-attributed landing (generic or sticker).
 * Accepts qr | qr_code | qrcode | qr_sticker.
 */
export function isQrAttributionSource(
  source: string | string[] | null | undefined
): boolean {
  return getQrAttributionKind(source) != null;
}

/**
 * Distinguish sticker vs generic QR for analytics (`qr_source`).
 * Returns null when the source is not QR-attributed.
 */
export function getQrAttributionKind(
  source: string | string[] | null | undefined
): QrAttributionKind | null {
  const normalized = firstSourceValue(source);
  if (!normalized) return null;
  if (normalized === "qr_sticker") return "sticker";
  if (
    normalized === "qr" ||
    normalized === "qr_code" ||
    normalized === "qrcode"
  ) {
    return "qr";
  }
  return null;
}
